import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Languages, Download, ArrowLeft } from 'lucide-react';
import { atsCvTranslations } from './atsTranslations';
import './App.ats.css';

const ATSCV = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialLanguage = () => {
    const langParam = searchParams.get('lang');
    if (langParam === 'en' || langParam === 'fr') return langParam;
    return 'fr';
  };

  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (langParam === 'en' || langParam === 'fr') setLanguage(langParam);
  }, [searchParams]);

  const t = atsCvTranslations[language];

  const handleDownloadPDF = () => window.print();

  const SectionTitle = ({ children }) => (
    <h2 className="ats-section-title">{children}</h2>
  );

  return (
    <div className="ats-page">
      <article className="ats-container">
        <header className="ats-header">
          <div className="ats-header-top">
            <div>
              <h1 className="ats-name">Nizar Tazi</h1>
              <p className="ats-title">{t.title}</p>
            </div>
            <button type="button" onClick={handleDownloadPDF} className="ats-download no-print">
              <Download size={14} aria-hidden="true" />
              {t.downloadPDF}
            </button>
          </div>

          <address className="ats-contact not-italic">
            <p>
              Tanger, Morocco |{' '}
              <a href="mailto:Nizar.tazi@hotmail.fr">Nizar.tazi@hotmail.fr</a> |{' '}
              <a href="tel:+212779168753">+212779168753</a>
            </p>
            <p>
              <a href="https://linkedin.com/in/nizar-tazi/" target="_blank" rel="noopener noreferrer">
                linkedin.com/in/nizar-tazi
              </a>
              {' | '}
              <a href="https://github.com/NizarTazi/" target="_blank" rel="noopener noreferrer">
                github.com/NizarTazi
              </a>
              {' | '}
              <a href="https://nizart.works" target="_blank" rel="noopener noreferrer">
                nizart.works
              </a>
            </p>
          </address>
        </header>

        <main className="ats-body">
          <section className="ats-section">
            <SectionTitle>{t.summary}</SectionTitle>
            <p className="ats-text">{t.summaryText}</p>
          </section>

          <section className="ats-section">
            <SectionTitle>{t.experience}</SectionTitle>
            {t.experiences.map((exp, index) => (
              <div key={index} className="ats-entry">
                <div className="ats-entry-head">
                  <h3 className="ats-entry-title">{exp.title}</h3>
                  <span className="ats-entry-period">{exp.period}</span>
                </div>
                <p className="ats-entry-meta">
                  {exp.company} | {exp.location}
                </p>
                <ul className="ats-list">
                  {exp.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <section className="ats-section ats-section--compact">
            <SectionTitle>{t.education}</SectionTitle>
            {t.education_items.map((edu, index) => (
              <p key={index} className="ats-line">
                <strong>{edu.degree}</strong> | {edu.school}, {edu.location} | {edu.year}
              </p>
            ))}
          </section>

          <section className="ats-section ats-section--compact">
            <SectionTitle>{t.skills}</SectionTitle>
            {t.skills_list.map((skill, index) => (
              <p key={index} className="ats-line">{skill}</p>
            ))}
          </section>

          <section className="ats-section ats-section--compact">
            <SectionTitle>{t.projects}</SectionTitle>
            {t.projects_items.map((project, index) => (
              <p key={index} className="ats-line">
                <strong>{project.title}</strong> | {project.description}
              </p>
            ))}
          </section>

          <section className="ats-section ats-section--compact ats-section--last">
            <SectionTitle>{t.languages}</SectionTitle>
            <p className="ats-line">
              {t.languages_items.map((lang, index) => (
                <span key={index}>
                  {index > 0 && ' | '}
                  <strong>{lang.lang}</strong>: {lang.level}
                </span>
              ))}
            </p>
          </section>
        </main>
      </article>

      <div className="ats-actions no-print">
        <div className="ats-actions-group">
          <button type="button" className="ats-action-btn ats-action-btn--lang">
            <Languages size={16} aria-hidden="true" />
            {language.toUpperCase()}
          </button>
          <div className="ats-lang-menu">
            <button
              type="button"
              onClick={() => {
                setLanguage('en');
                setSearchParams({ lang: 'en' });
              }}
              className={language === 'en' ? 'active' : ''}
            >
              English
            </button>
            <button
              type="button"
              onClick={() => {
                setLanguage('fr');
                setSearchParams({ lang: 'fr' });
              }}
              className={language === 'fr' ? 'active' : ''}
            >
              Français
            </button>
          </div>
        </div>
        <Link to="/" className="ats-action-btn ats-action-btn--primary">
          <ArrowLeft size={16} aria-hidden="true" />
          {t.portfolio}
        </Link>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }

          html, body, #root, .App {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }

          .ats-page {
            padding: 9mm 12mm !important;
            background: #fff !important;
          }

          .ats-container {
            padding: 0 !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ATSCV;
